import { FIELD_TYPE_LABELS, FIELD_TYPES, type FieldType } from '@fundline/shared/fields/values';
import {
  fieldKeyFromLabel,
  FieldLabelSchema,
  FieldOptionsSchema,
  NewFieldSchema,
  parseOptionLines,
} from '@fundline/shared/schemas/fields';
import { type FormEvent, useId, useState } from 'react';
import { Alert } from '../../components/ui/Alert.tsx';
import { Button } from '../../components/ui/Button.tsx';
import { CONTROL, SelectField, TextField } from '../../components/ui/Field.tsx';
import { Card, EmptyState, PageHeader, Spinner } from '../../components/ui/Layout.tsx';
import { useWorkspaceClaims } from '../../lib/auth.tsx';
import { SettingsTabs } from './SettingsTabs.tsx';
import { type FieldDefinition, useFieldDefinitionsQuery, useFieldMutations } from './use-fields.ts';

const TYPE_OPTIONS = FIELD_TYPES.map((t) => ({ value: t, label: FIELD_TYPE_LABELS[t] }));

function OptionsArea({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
}) {
  const id = useId();
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="callout font-medium text-ink">
        Options (one per line)
      </label>
      <textarea
        id={id}
        rows={4}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-error` : `${id}-hint`}
        className={`${CONTROL} py-2`}
      />
      {error ? (
        <p id={`${id}-error`} className="footnote text-danger">
          {error}
        </p>
      ) : (
        <p id={`${id}-hint`} className="footnote text-ink-muted">
          Plain characters keep texts that use this field to one segment (avoid curly quotes, long
          dashes and emoji).
        </p>
      )}
    </div>
  );
}

export function FieldsSettingsPage() {
  const { workspaceId } = useWorkspaceClaims();
  const fields = useFieldDefinitionsQuery(workspaceId);

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Workspace settings"
        description="Fields every merchant profile has. Removing a field hides it; values are kept."
      />
      <SettingsTabs />
      {fields.isPending ? <Spinner label="Loading fields" /> : null}
      {fields.isError ? <Alert tone="error">{fields.error.message}</Alert> : null}
      {fields.data ? <FieldsEditor fields={fields.data} workspaceId={workspaceId} /> : null}
    </div>
  );
}

function FieldsEditor({ fields, workspaceId }: { fields: FieldDefinition[]; workspaceId: string }) {
  const { create, update, reorder } = useFieldMutations(workspaceId);
  const active = fields.filter((f) => !f.archivedAt);
  const archived = fields.filter((f) => f.archivedAt);
  const [editing, setEditing] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function run(fn: () => Promise<unknown>, success: string): Promise<boolean> {
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(success);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.');
      return false;
    }
  }

  function move(index: number, delta: -1 | 1) {
    const ids = active.map((f) => f.id);
    const target = index + delta;
    const a = ids[index];
    const b = ids[target];
    if (a === undefined || b === undefined) return;
    ids[index] = b;
    ids[target] = a;
    void run(() => reorder.mutateAsync(ids), 'Order saved.');
  }

  const busy = create.isPending || update.isPending || reorder.isPending;

  return (
    <>
      <AddFieldCard
        busy={create.isPending}
        onAdd={(input) =>
          run(
            () =>
              create.mutateAsync({
                ...input,
                key: fieldKeyFromLabel(
                  input.label,
                  fields.map((f) => f.key),
                ),
                position: Math.max(0, ...fields.map((f) => f.position)) + 1,
              }),
            `Added "${input.label}".`,
          )
        }
      />

      <section className="flex flex-col gap-3" aria-label="Fields">
        <h2 className="body font-semibold text-ink">Fields</h2>
        {error ? <Alert tone="error">{error}</Alert> : null}
        {notice ? <Alert tone="success">{notice}</Alert> : null}
        {active.length === 0 ? <EmptyState title="No fields yet" /> : null}
        <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
          {active.map((field, index) => (
            <li key={field.id} className="p-3" data-testid="field-row">
              {editing === field.id ? (
                <EditFieldForm
                  field={field}
                  busy={update.isPending}
                  onCancel={() => setEditing(null)}
                  onSave={(patch) =>
                    run(async () => {
                      await update.mutateAsync({ id: field.id, patch });
                      setEditing(null);
                    }, 'Field saved.')
                  }
                />
              ) : (
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="truncate font-medium text-ink">{field.label}</p>
                    <p className="flex flex-wrap gap-1 footnote text-ink-muted">
                      <span>{FIELD_TYPE_LABELS[field.type]}</span>
                      {field.type === 'dropdown' ? (
                        <span>· {field.options.length} options</span>
                      ) : null}
                      {field.systemKey ? (
                        <span className="rounded-sm bg-surface px-1.5 font-medium text-ink-muted">
                          Built-in
                        </span>
                      ) : null}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    <Button
                      variant="ghost"
                      aria-label={`Move ${field.label} up`}
                      disabled={busy || index === 0}
                      onClick={() => move(index, -1)}
                    >
                      ↑
                    </Button>
                    <Button
                      variant="ghost"
                      aria-label={`Move ${field.label} down`}
                      disabled={busy || index === active.length - 1}
                      onClick={() => move(index, 1)}
                    >
                      ↓
                    </Button>
                    <Button
                      variant="secondary"
                      aria-label={`Edit ${field.label}`}
                      disabled={busy}
                      onClick={() => setEditing(field.id)}
                    >
                      Edit
                    </Button>
                    {field.systemKey ? null : (
                      <Button
                        variant="danger"
                        size="sm"
                        aria-label={`Remove ${field.label}`}
                        disabled={busy}
                        onClick={() => {
                          if (
                            !window.confirm(
                              `Remove "${field.label}"? It disappears from profiles; existing values are kept and you can restore it later.`,
                            )
                          )
                            return;
                          void run(
                            () =>
                              update.mutateAsync({
                                id: field.id,
                                patch: { archived_at: new Date().toISOString() },
                              }),
                            `Removed "${field.label}".`,
                          );
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>

      {archived.length > 0 ? (
        <section className="flex flex-col gap-3" aria-label="Removed fields">
          <h2 className="body font-semibold text-ink">Removed fields</h2>
          <ul className="flex flex-col divide-y divide-hairline rounded-lg border border-hairline bg-surface-raised">
            {archived.map((field) => (
              <li
                key={field.id}
                className="flex items-center justify-between gap-2 p-3"
                data-testid="removed-field-row"
              >
                <span className="min-w-0 truncate text-ink-muted">
                  {field.label}{' '}
                  <span className="footnote text-ink-muted">({FIELD_TYPE_LABELS[field.type]})</span>
                </span>
                <Button
                  variant="secondary"
                  aria-label={`Restore ${field.label}`}
                  disabled={busy}
                  onClick={() =>
                    void run(
                      () =>
                        update.mutateAsync({
                          id: field.id,
                          patch: {
                            archived_at: null,
                            position: Math.max(0, ...active.map((f) => f.position)) + 1,
                          },
                        }),
                      `Restored "${field.label}".`,
                    )
                  }
                >
                  Restore
                </Button>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

function AddFieldCard({
  busy,
  onAdd,
}: {
  busy: boolean;
  onAdd: (input: { label: string; type: FieldType; options: string[] }) => Promise<boolean>;
}) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState<FieldType>('text');
  const [optionsText, setOptionsText] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const parsed = NewFieldSchema.safeParse({
      label,
      type,
      options: type === 'dropdown' ? parseOptionLines(optionsText) : [],
    });
    if (!parsed.success) {
      setErrors(Object.fromEntries(parsed.error.issues.map((i) => [String(i.path[0]), i.message])));
      return;
    }
    setErrors({});
    if (!(await onAdd(parsed.data))) return;
    setLabel('');
    setOptionsText('');
    setType('text');
  }

  return (
    <Card title="Add a field">
      <form className="flex flex-col gap-4" onSubmit={(e) => void onSubmit(e)} noValidate>
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField
            label="Label"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            error={errors.label}
          />
          <SelectField
            label="Type"
            hint="The type cannot change once merchants have values."
            value={type}
            onChange={(e) => setType(e.target.value as FieldType)}
            options={TYPE_OPTIONS}
            error={errors.type}
          />
        </div>
        {type === 'dropdown' ? (
          <OptionsArea value={optionsText} onChange={setOptionsText} error={errors.options} />
        ) : null}
        <div>
          <Button type="submit" busy={busy}>
            Add field
          </Button>
        </div>
      </form>
    </Card>
  );
}

function EditFieldForm({
  field,
  busy,
  onSave,
  onCancel,
}: {
  field: FieldDefinition;
  busy: boolean;
  onSave: (patch: { label: string; options?: string[] }) => Promise<unknown>;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(field.label);
  const [optionsText, setOptionsText] = useState(field.options.join('\n'));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const editOptions = field.type === 'dropdown' && !field.systemKey;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const next: Record<string, string> = {};
    const l = FieldLabelSchema.safeParse(label);
    if (!l.success) next.label = l.error.issues[0]?.message ?? 'Invalid label';
    let options: string[] | undefined;
    if (editOptions) {
      const o = FieldOptionsSchema.safeParse(parseOptionLines(optionsText));
      if (!o.success) next.options = o.error.issues[0]?.message ?? 'Invalid options';
      else if (o.data.length === 0) next.options = 'Add at least one option';
      else options = o.data;
    }
    setErrors(next);
    if (Object.keys(next).length > 0 || !l.success) return;
    await onSave(options ? { label: l.data, options } : { label: l.data });
  }

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => void onSubmit(e)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onCancel();
      }}
      noValidate
    >
      <TextField
        label="Label"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        error={errors.label}
        autoFocus
      />
      {editOptions ? (
        <OptionsArea value={optionsText} onChange={setOptionsText} error={errors.options} />
      ) : null}
      {field.systemKey ? (
        <p className="footnote text-ink-muted">Built-in field: only the label can change.</p>
      ) : null}
      <div className="flex gap-2">
        <Button type="submit" busy={busy}>
          Save
        </Button>
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}
